import React, { useEffect, useRef } from 'react';
import {
  HubConnectionBuilder,
  LogLevel,
  HttpTransportType
} from '@microsoft/signalr';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { addInfo } from '../../actions/snackbar/snackbar';
import { CurrentUserSelector } from '../../selectors/userSelectors';
import { isKnownTaskType } from '../../../types/realtime';
import type { UserNotificationDto } from '../../../types/realtime';
import { getUrlFromSwaggerUrl } from '../../apis/api';
import { I18nNamespace } from '../../../i18n/types/i18nNamespace';
import { createAccessTokenProvider } from '@/app/services/authToken';
import { authTelemetry } from '@/app/services/authTelemetry';
import type { AppDispatch } from '@/app/store';
import { apiLoginRequest, clearLogoutFlag, hasUserLoggedOut } from '@/authConfig.ts';
import { dispatchKnownNotificationHandler, handleFailure } from './notificationHandlers';

/** Resolved hub URL: "<api-base>/hubs/notifications" */
const getWebsocketUrl = (): string =>
  `${getUrlFromSwaggerUrl(process.env.REACT_APP_SCENES)}/hubs/notifications` || '';

/**
 * Provides a single SignalR connection that lives for the whole
 * authenticated session.  Children don't need the connection object,
 * so nothing is exposed – the provider only handles side effects.
 */
const NotificationHubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(I18nNamespace.Common);
  const currentUser = useSelector(CurrentUserSelector);
  const { instance } = useMsal();
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    /** Abort when the user is not yet known (or logged out). */
    if (!currentUser?.id) return;

    const accessTokenProvider = createAccessTokenProvider({
      msalInstance: instance,
      apiLoginRequest,
      authTelemetry,
      hasUserLoggedOut,
      clearLogoutFlag
    });

    const connection = new HubConnectionBuilder()
      .configureLogging(LogLevel.Warning)
      .withUrl(getWebsocketUrl(), {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () =>
          accessTokenProvider().catch((err) => {
            console.warn('[NOTIFICATION.HUB] failed to acquire access token', err);
            return '';
          })
      })
      .withAutomaticReconnect()
      .build();

    
    connection.keepAliveIntervalInMilliseconds = 1000 * 15; // 15 seconds
    connection.serverTimeoutInMilliseconds    = 1000 * 60 * 20; // 20 minutes

    const handleNotification = (msg: UserNotificationDto) => {
      if (msg.status === 'Failed') {
        handleFailure(msg, dispatch);
        return;
      }

      if (isKnownTaskType(msg.taskType)) {
        const typedMsg = msg as UserNotificationDto & { taskType: typeof msg.taskType };
        if (dispatchKnownNotificationHandler(typedMsg, dispatch, t)) {
          return;
        }
      }

      dispatch(addInfo({ infoText: `✅ ${msg.taskType} completed` }));
    };

    connection.on('UserNotification', handleNotification);

    /* Connection life-cycle ------------------------------------------------ */
    const start = async (): Promise<void> => {
      try {
        await connection.start();
      } catch (err) {
        console.error('[NOTIFICATION.HUB] start failed', err);
      }
    };
    start();

    // If we stay in "Reconnecting" for 30 s → warn user.
    connection.onreconnecting(() => {
      reconnectTimer.current = setTimeout(() => {
       // dispatch(addInfo({ infoText: 'Live updates unavailable' }));
      }, 30_000);
    });

    connection.onreconnected(() => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = undefined;
      }
      //dispatch(addInfo({ infoText: 'Live updates restored' }));
    });

    // Clean-up on unmount / logout
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      // Stop gracefully; ignore failures during teardown.
      connection
        .stop()
        .catch((err) => console.debug('[NOTIFICATION.HUB] stop error', err));
    };
  }, [currentUser?.id, dispatch, instance]);

  return <>{children}</>;
};

export default NotificationHubProvider;
